/**
 * planes.js — CRUD de planes de suscripción.
 *
 * Cada plan define qué módulos incluye. Cuando se asigna un plan a una empresa,
 * se pueden sincronizar los modulosDisponibles automáticamente.
 *
 * Colección: planes/{planId}
 * Estructura: { id, nombre, color, orden, modulosIncluidos[], descripcion, activo, creadoEn, actualizadoEn }
 */
import { collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS } from './constants'
import { forPlatformCreate, forUpdate } from './dbHelpers'
import { createLogger } from './logger'
import { leerColeccionConCota } from './lecturaDirectaConCota'
import { UMBRAL_DIRECTORIO_DE_LA_PLATAFORMA } from './cotaDeLectura'
import { listaDisponiblesAEscribir, quitaModulos } from './moduloContratado'

const log = createLogger('planes')

const COL = COLLECTIONS.PLANES

// ── Read ──

export async function getPlanes() {
  const snap = await getDocs(query(collection(db, COL), orderBy('orden', 'asc')))
  return snap.docs.map(docConId)
}

export async function getPlan(planId) {
  const snap = await getDoc(doc(db, COL, planId))
  return snap.exists() ? docConId(snap) : null
}

// ── Create ──

export async function crearPlan(data) {
  const id = data.id || data.nombre.toLowerCase().replace(/[^a-z0-9]+/g, '_')
  const payload = forPlatformCreate({
    nombre: data.nombre,
    color: data.color || '#6b7280',
    orden: data.orden ?? 99,
    modulosIncluidos: data.modulosIncluidos || [],
    descripcion: data.descripcion || '',
    // P1.4 — `syncConfig` NO viaja en el alta: las rules prohíben campos de
    // gasto en el create, así que un plan nace NEUTRO (todos los endpoints
    // 'off', sin presupuesto de IA ni cloud). El caller lo habilita después
    // con el callable `actualizarPlan`, que audita quién lo hizo.
    activo: true,
  })
  await setDoc(doc(db, COL, id), payload)
  return { id, ...payload }
}

// ── Update ──

export async function actualizarPlan(id, data) {
  await updateDoc(doc(db, COL, id), forUpdate(data))
}

// ── Delete ──

export async function eliminarPlan(planId) {
  await deleteDoc(doc(db, COL, planId))
}

// ── Pertenencia al plan ──

/**
 * El plan de una empresa, con la misma precedencia que el backend
 * (`functions/syncCore/syncGating.js`, `leerPlan`): `planId` gana. Mirando sólo
 * `plan`, una empresa con `planId: 'premium'` y sin `plan` contaba como `free`
 * y la «Sincronización» de `free` le pisaba los módulos.
 */
export function planDeEmpresa(empresa) {
  return empresa?.planId || empresa?.plan || 'free'
}

// ── La lista de módulos de las empresas: la escribe el servidor ──
//
// Report 9G7ROjEWxtjTjhF6tfiW. Decisión de Mariano (20 y 29-sep-2026): antes de
// reescribir la lista de módulos de una o muchas empresas, AVISAR —no bloquear—
// cuántas pierden el módulo de un canal que tienen conectado y vivo. El cálculo
// es del servidor (`simularCambioModulos`): las conexiones están cerradas al
// navegador.
//
// Desde la tajada T5 de C5nW5b0MoxBoq9Vw4Vob la escritura también es del
// servidor: el callable `escribirModulosEmpresas` (`functions/planesAdmin.js`)
// gatea con `planes.editar` y el alcance por empresa, recalcula el aviso,
// filtra los activados, escribe `plan`/`planId` y deja la auditoría. Acá queda
// el aviso previo, para que la persona lo vea antes de mandar. El guard
// (`planes.escritorUnico.guard.test.js`) rompe si un archivo de `src/` escribe
// `modulosDisponibles`, `planId` o `plan`.

// En la EMPRESA, lista vacía o ausente = VE TODO (`ModulosToggle.jsx`,
// `functions/lib/perdidaModulosCanal.js`). Un plan sin módulos, o sacarle a una
// empresa el último, se simula y se escribe como `[MODULOS_NINGUNO]` (decisión
// de Mariano, 23-oct-2026, report 0TB62OPRQUDyqQuwpRtM); la regla
// —`listaDisponiblesAEscribir` y `quitaModulos`— vive en `moduloContratado.js`,
// y el servidor aplica la misma (paridad en `moduloContratado.test.js`).

// Tope de `cambios` por pedido de los dos callables (`MAX_CAMBIOS_SIMULACION`,
// `functions/planesAdmin.js`). Más empresas van en varios pedidos: recortar
// dejaría parte del cambio sin mirar.
const MAX_CAMBIOS_POR_PEDIDO = 2000

async function simular(cambios) {
  const fn = httpsCallable(functions, 'simularCambioModulos')
  const total = { verificable: true, evaluadas: 0, conConexion: 0, afectadas: [], noVerificables: [] }
  for (let i = 0; i < cambios.length; i += MAX_CAMBIOS_POR_PEDIDO) {
    const { data } = await fn({ cambios: cambios.slice(i, i + MAX_CAMBIOS_POR_PEDIDO) })
    if (!data?.verificable) return { verificable: false, motivo: data?.motivo || 'respuesta-sin-verificar' }
    total.evaluadas += data.evaluadas
    total.conConexion += data.conConexion
    total.afectadas.push(...data.afectadas)
    total.noVerificables.push(...data.noVerificables)
  }
  return total
}

/**
 * PURO. El texto del aviso a partir de lo que contestó la simulación, o `null`
 * si nadie pierde nada y todo se pudo verificar. Nunca dice «nadie pierde»
 * cuando no lo sabe: sin verificación, o con empresas que no se pudieron
 * evaluar, avisa.
 */
export function textoDelAviso(sim) {
  if (!sim?.verificable) {
    return `No se pudo verificar si alguna empresa pierde el módulo de un canal que tiene conectado (${sim?.motivo || 'sin respuesta'}). Si seguís, el cambio se escribe sin esa verificación.`
  }
  const partes = []
  if (sim.afectadas.length > 0) {
    const detalle = sim.afectadas
      .map((a) => `${a.nombre || a.empresaId} (${a.plataformas.map((p) => `${p.plataformaId} → ${p.modulo}`).join(', ')})`)
      .join('; ')
    partes.push(`${sim.afectadas.length} de ${sim.conConexion} empresas con un canal conectado pierden el módulo de ese canal (de ${sim.evaluadas} evaluadas): ${detalle}. Su conexión sigue viva pero dejan de ver el canal.`)
  }
  if (sim.noVerificables.length > 0) {
    const detalle = sim.noVerificables
      .map((n) => `${n.empresaId}${n.plataformaId ? ` / ${n.plataformaId}` : ''} (${n.causa})`)
      .join('; ')
    partes.push(`No se pudo verificar ${sim.noVerificables.length}: ${detalle}.`)
  }
  return partes.length > 0 ? partes.join(' ') : null
}

/**
 * La escritura no terminó. `escritas` es lo que el servidor confirmó con sus
 * campos (para `aplicarEscritas`); `cantidad` cuenta además las que una tanda
 * cortada dejó escritas sin devolverlas, y `total` es lo pedido. La pantalla
 * muestra el mensaje y vuelve a leer las empresas.
 */
export class EscrituraIncompleta extends Error {
  constructor({ escritas, cantidad, total, causa }) {
    super(cantidad > 0
      ? `Se escribieron ${cantidad} de ${total} empresas; el resto quedó como estaba.`
      : `No se escribió ninguna de las ${total} empresas${causa?.code ? ` (${causa.code})` : ''}.`)
    this.name = 'EscrituraIncompleta'
    Object.assign(this, { escritas, cantidad, total, causa })
  }
}

// Con `planId` el servidor lee la lista del plan: no viaja la de la pantalla.
const aPedido = (c) => (c.planId ? { empresaId: c.empresa.id, planId: c.planId } : { empresaId: c.empresa.id, modulosDisponibles: c.modulosDisponibles })

/**
 * Un pedido al callable. Si el servidor rebota con SU aviso (algo cambió desde
 * la simulación), se muestra y, si la persona sigue, se reintenta el MISMO
 * pedido con `avisoAceptado`; el sí no se arrastra a los pedidos siguientes.
 * @returns {Promise<Array<{id: string, campos: object}>|null>} null si canceló.
 */
async function escribirPedido(fn, cambios, avisoAceptado, confirmar) {
  try {
    const { data } = await fn({ cambios, avisoAceptado })
    return data.escritas
  } catch (e) {
    if (avisoAceptado || e?.details?.razon !== 'aviso-sin-aceptar') throw e
    if (!(await confirmar(textoDelAviso(e.details.aviso)))) return null
    const { data } = await fn({ cambios, avisoAceptado: true })
    return data.escritas
  }
}

/**
 * Cambia la lista de módulos de una o varias empresas, avisando antes si
 * alguna pierde el módulo de un canal conectado.
 *
 * @param {Array<{empresa: object, modulosDisponibles: string[], planId?: string}>} cambios
 *   `empresa` como la tiene la pantalla; `modulosDisponibles`, la lista que
 *   queda (con ella se simula el aviso). Con `planId` es un cambio de plan o
 *   «Sincronizar»: el servidor escribe la lista del plan y `plan`/`planId`.
 * @param {{confirmar: (aviso: string) => Promise<boolean>}} opciones
 *   `confirmar` muestra el aviso y resuelve `true` si la persona sigue.
 * @returns {Promise<{cancelado: boolean, escritas: Array<{id: string, campos: object}>}>}
 *   `escritas`, lo que escribió el servidor, para `aplicarEscritas`. Cancelar a
 *   mitad de varios pedidos devuelve lo que ya quedó escrito.
 * @throws {EscrituraIncompleta} si un pedido falla: dice cuántas quedaron.
 */
export async function escribirModulosEmpresas(cambios, { confirmar }) {
  const total = cambios.length
  let avisoAceptado = false
  const quitan = cambios.filter((c) => quitaModulos(c.empresa.modulosDisponibles, c.modulosDisponibles))
  if (quitan.length > 0) {
    let sim
    try {
      // La lista que se va a escribir: `[MODULOS_NINGUNO]` le dice al server
      // que se pierde todo (con `[]` habría contestado «ve todo, nadie pierde»).
      sim = await simular(quitan.map((c) => ({ empresaId: c.empresa.id, modulosNuevos: listaDisponiblesAEscribir(c.modulosDisponibles) })))
    } catch (e) {
      log.warn('simulación de módulos falló', { code: e?.code, message: e?.message })
      sim = { verificable: false, motivo: e?.code || 'error' }
    }
    const aviso = textoDelAviso(sim)
    if (aviso) {
      if (!(await confirmar(aviso))) return { cancelado: true, escritas: [] }
      avisoAceptado = true
    }
  }

  const fn = httpsCallable(functions, 'escribirModulosEmpresas')
  const escritas = []
  for (let i = 0; i < total; i += MAX_CAMBIOS_POR_PEDIDO) {
    let deEste
    try {
      deEste = await escribirPedido(fn, cambios.slice(i, i + MAX_CAMBIOS_POR_PEDIDO).map(aPedido), avisoAceptado, confirmar)
    } catch (e) {
      const cortadas = Array.isArray(e?.details?.escritas) ? e.details.escritas.length : 0
      log.warn('escritura de módulos incompleta', { code: e?.code, escritas: escritas.length + cortadas, total })
      throw new EscrituraIncompleta({ escritas, cantidad: escritas.length + cortadas, total, causa: e })
    }
    if (deEste === null) return { cancelado: true, escritas }
    escritas.push(...deEste)
  }
  return { cancelado: false, escritas }
}

/**
 * PURO. Lo que la pantalla le dice a la persona cuando la escritura falló: la
 * cuenta de `EscrituraIncompleta` («N de M») o el texto por defecto.
 */
export function textoDeFalla(e, porDefecto) {
  return e instanceof EscrituraIncompleta ? e.message : porDefecto
}

/** PURO. ¿Quedó algo escrito que la pantalla no tiene? Entonces vuelve a leer las empresas. */
export const hayQueReleer = (e) => e instanceof EscrituraIncompleta && e.cantidad > 0

/**
 * Las empresas que las pantallas de Planes y Módulos muestran: todas menos las
 * dadas de baja. Es el directorio entero a propósito (cambiar el plan o
 * sincronizar mira todas): el cap avisa, no recorta.
 */
export async function leerEmpresasVigentes() {
  const snap = await leerColeccionConCota({
    collectionName: COLLECTIONS.EMPRESAS,
    ref: collection(db, COLLECTIONS.EMPRESAS),
    cap: UMBRAL_DIRECTORIO_DE_LA_PLATAFORMA,
  })
  return snap.docs.map(docConId).filter((e) => e.estado !== 'baja')
}

/** PURO. El estado local de la pantalla con lo que `escribirModulosEmpresas` escribió. */
export function aplicarEscritas(empresas, escritas) {
  const porId = new Map(escritas.map((e) => [e.id, e.campos]))
  return empresas.map((e) => (porId.has(e.id) ? { ...e, ...porId.get(e.id) } : e))
}

// ── Sincronizar módulos del plan a todas las empresas que lo tengan ──

/** Las empresas del plan cuya lista difiere de la del plan, como `cambios`. */
export function cambiosDeSincronizacion(planId, modulosIncluidos, empresas) {
  return empresas
    .filter((e) => planDeEmpresa(e) === planId)
    .filter((e) => {
      // Se compara contra lo que se escribiría: una empresa ya en «ninguno»
      // con el plan sin módulos no se reescribe; una vieja en `[]`, sí.
      const current = e.modulosDisponibles || []
      const objetivo = listaDisponiblesAEscribir(modulosIncluidos)
      return !(current.length === objetivo.length && objetivo.every((m) => current.includes(m)))
    })
    .map((empresa) => ({ empresa, modulosDisponibles: modulosIncluidos, planId }))
}

export async function sincronizarModulosPlan(planId, modulosIncluidos, empresas, opciones) {
  return escribirModulosEmpresas(cambiosDeSincronizacion(planId, modulosIncluidos, empresas), opciones)
}

// ── Seed: crear los 3 planes default si no existen ──

export const PLANES_DEFAULT = [
  {
    id: 'free',
    nombre: 'Free',
    color: '#10b981',
    orden: 1,
    modulosIncluidos: ['gestion', 'comunicaciones'],
    descripcion: 'Plan básico con gestión y mensajería',
  },
  {
    id: 'pro',
    nombre: 'Pro',
    color: '#3b82f6',
    orden: 2,
    modulosIncluidos: ['gestion', 'comunicaciones', 'diagnostico'],
    descripcion: 'Incluye diagnóstico y frameworks',
  },
  {
    id: 'premium',
    nombre: 'Premium',
    color: '#f59e0b',
    orden: 3,
    modulosIncluidos: ['gestion', 'comunicaciones', 'diagnostico', 'mercadolibre', 'consultoria', 'reportes-generados'],
    descripcion: 'Acceso completo a todos los módulos',
  },
]

export async function seedPlanesDefault() {
  const existing = await getPlanes()
  const existingIds = existing.map(p => p.id)
  let created = 0

  for (const plan of PLANES_DEFAULT) {
    if (existingIds.includes(plan.id)) continue
    await crearPlan(plan)
    created++
  }

  return created
}
